<template>
  <div
    ref="wrapper"
    class="yt-wrapper"
    :class="{ 'yt-fullscreen': isFullscreen }"
    @mousemove="showControls"
    @touchstart="showControls"
  >
    <!-- Área visível sempre em 16:9 -->
    <div class="yt-stage">
      <!-- O SDK do YouTube substitui esta div pelo iframe. O iframe é 2x mais alto que a área
           visível: o vídeo fica centralizado do mesmo tamanho e as faixas de cima (título,
           compartilhar) e de baixo ("Mais vídeos") ficam fora da área visível. -->
      <div class="yt-frame"><div ref="player"></div></div>

      <!-- Camada transparente: impede cliques no título, compartilhar, logo e "Assistir no YouTube" -->
      <div class="yt-shield" @click="togglePlay"></div>

      <!-- Capa: cobre o vídeo antes de iniciar, ao pausar e ao terminar -->
      <div
        v-if="showCover"
        class="yt-cover"
        :style="{ backgroundImage: `url(${thumbnail})` }"
        @click="play"
      >
        <button type="button" class="yt-big-play" :aria-label="t.play">
          <b-icon :icon="ended ? 'arrow-clockwise' : 'play-fill'"></b-icon>
        </button>
      </div>
    </div>

    <button
      v-if="ready && muted && playing"
      type="button"
      class="yt-unmute"
      @click="toggleMute"
    >
      <b-icon icon="volume-mute-fill"></b-icon> {{ t.unmute }}
    </button>

    <!-- Controles próprios -->
    <div v-if="ready" class="yt-controls" :class="{ 'yt-controls-hidden': !controlsVisible }">
      <input
        class="yt-progress"
        type="range"
        min="0"
        :max="duration || 0"
        step="0.1"
        :value="currentTime"
        :style="{ '--yt-progress': progressPercent + '%' }"
        :aria-label="t.progress"
        @input="onSeekInput"
        @change="onSeekCommit"
      />

      <div class="yt-bar">
        <button type="button" class="yt-btn" :aria-label="playing ? t.pause : t.play" @click="togglePlay">
          <b-icon :icon="playing ? 'pause-fill' : 'play-fill'"></b-icon>
        </button>

        <button type="button" class="yt-btn" :aria-label="muted ? t.unmute : t.mute" @click="toggleMute">
          <b-icon :icon="muted ? 'volume-mute-fill' : 'volume-up-fill'"></b-icon>
        </button>
        <input
          class="yt-volume d-none d-md-block"
          type="range"
          min="0"
          max="100"
          :value="muted ? 0 : volume"
          :aria-label="t.volume"
          @input="onVolume"
        />

        <button
          v-if="isLive"
          type="button"
          class="yt-live"
          :class="{ 'yt-live-behind': !atLiveEdge }"
          @click="goLive"
        >
          <span class="yt-live-dot"></span>{{ atLiveEdge ? t.live : t.backToLive }}
        </button>
        <span v-else class="yt-time">{{ format(currentTime) }} / {{ format(duration) }}</span>

        <span class="flex-grow-1"></span>

        <button
          v-if="fullscreenSupported"
          type="button"
          class="yt-btn"
          :aria-label="t.fullscreen"
          @click="toggleFullscreen"
        >
          <b-icon :icon="isFullscreen ? 'fullscreen-exit' : 'fullscreen'"></b-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

const LABELS = {
  pt: { play: 'Reproduzir', pause: 'Pausar', mute: 'Silenciar', unmute: 'Ativar som', volume: 'Volume', progress: 'Progresso', live: 'AO VIVO', backToLive: 'VOLTAR AO VIVO', fullscreen: 'Tela cheia' },
  esp: { play: 'Reproducir', pause: 'Pausar', mute: 'Silenciar', unmute: 'Activar sonido', volume: 'Volumen', progress: 'Progreso', live: 'EN VIVO', backToLive: 'VOLVER AL VIVO', fullscreen: 'Pantalla completa' },
  en: { play: 'Play', pause: 'Pause', mute: 'Mute', unmute: 'Unmute', volume: 'Volume', progress: 'Progress', live: 'LIVE', backToLive: 'GO TO LIVE', fullscreen: 'Fullscreen' }
}

// Quantos segundos além da latência natural da live para considerar "atrasado"
const LIVE_EDGE_TOLERANCE = 10

let apiPromise = null
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT)
  if (apiPromise) return apiPromise
  apiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous()
      resolve(window.YT)
    }
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    tag.async = true
    tag.onerror = () => {
      apiPromise = null
      reject(new Error('Falha ao carregar o SDK do YouTube'))
    }
    document.head.appendChild(tag)
  })
  return apiPromise
}

export function getYouTubeId(url) {
  if (!url) return null
  if (/^[\w-]{11}$/.test(url)) return url
  const match = String(url).match(
    /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|live\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/
  )
  return match ? match[1] : null
}

export default {
  name: 'YouTubePlayer',
  props: {
    url: { type: String, required: true }
  },
  data() {
    return {
      player: null,
      ready: false,
      playing: false,
      started: false,
      ended: false,
      muted: false,
      volume: 100,
      userInteracted: false,
      autoplayTimer: null,
      currentTime: 0,
      duration: 0,
      isLive: false,
      // Menor distância observada entre o fim da live e o tempo atual (latência natural do YouTube)
      minLag: null,
      seeking: false,
      controlsVisible: true,
      isFullscreen: false,
      timer: null,
      hideTimer: null
    }
  },
  computed: {
    ...mapState(['lang']),
    t() {
      return LABELS[this.lang] || LABELS.pt
    },
    videoId() {
      return getYouTubeId(this.url)
    },
    thumbnail() {
      return this.videoId ? `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg` : ''
    },
    showCover() {
      return !this.playing
    },
    progressPercent() {
      return this.duration ? Math.min(100, (this.currentTime / this.duration) * 100) : 0
    },
    atLiveEdge() {
      if (!this.started || this.minLag === null) return true
      return this.duration - this.currentTime - this.minLag < LIVE_EDGE_TOLERANCE
    },
    fullscreenSupported() {
      return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled)
    }
  },
  watch: {
    videoId(id) {
      if (this.player && id) {
        this.ended = false
        this.minLag = null
        this.player.loadVideoById(id)
      }
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
    if (!this.videoId) {
      console.warn('[YouTubePlayer] URL do YouTube inválida:', this.url)
      return
    }
    loadYouTubeApi()
      .then(YT => {
        if (this._isDestroyed) return
        this.player = new YT.Player(this.$refs.player, {
          videoId: this.videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            mute: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            rel: 0,
            iv_load_policy: 3,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.origin
          },
          events: {
            onReady: this.onReady,
            onStateChange: this.onStateChange
          }
        })
      })
      .catch(err => console.error(err))
  },
  beforeDestroy() {
    clearInterval(this.timer)
    clearTimeout(this.hideTimer)
    clearTimeout(this.autoplayTimer)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
    if (this.player && this.player.destroy) this.player.destroy()
  },
  methods: {
    onReady() {
      this.ready = true
      // Tenta iniciar com som no volume máximo
      this.player.unMute()
      this.player.setVolume(100)
      this.volume = 100
      this.muted = false
      this.player.playVideo()
      this.updateTime()
      this.timer = setInterval(this.updateTime, 250)
      // Se o navegador bloquear o autoplay com som, inicia mudo (volume continua 100)
      // e o botão "Ativar som" fica visível
      this.autoplayTimer = setTimeout(this.autoplayFallback, 2500)
    },
    autoplayFallback() {
      if (this.playing || this.userInteracted || !this.player) return
      this.player.mute()
      this.muted = true
      this.player.playVideo()
    },
    onStateChange(e) {
      const S = window.YT.PlayerState
      this.playing = e.data === S.PLAYING || e.data === S.BUFFERING
      if (this.playing) {
        this.started = true
        this.ended = false
        this.showControls()
      }
      if (e.data === S.ENDED) {
        this.ended = true
        this.controlsVisible = true
      }
      if (e.data === S.PAUSED) this.controlsVisible = true
      this.detectLive()
    },
    detectLive() {
      const data = this.player.getVideoData ? this.player.getVideoData() : null
      if (data && typeof data.isLive === 'boolean') this.isLive = data.isLive
    },
    updateTime() {
      if (!this.player || !this.player.getCurrentTime) return
      if (!this.isLive) this.detectLive()
      this.duration = this.player.getDuration() || 0
      if (!this.seeking) this.currentTime = this.player.getCurrentTime() || 0
      // Aprende a latência natural da live enquanto toca
      if (this.isLive && this.playing && !this.seeking && this.currentTime > 0 && this.duration > 0) {
        const lag = this.duration - this.currentTime
        if (this.minLag === null || lag < this.minLag) this.minLag = Math.max(0, lag)
      }
    },
    play() {
      if (!this.player) return
      this.userInteracted = true
      if (this.ended) this.player.seekTo(0, true)
      this.player.playVideo()
    },
    togglePlay() {
      if (!this.player) return
      this.userInteracted = true
      this.playing ? this.player.pauseVideo() : this.play()
    },
    toggleMute() {
      if (!this.player) return
      this.userInteracted = true
      if (this.muted) {
        this.player.unMute()
        if (!this.volume) this.volume = 100
        this.player.setVolume(this.volume)
      } else {
        this.player.mute()
      }
      this.muted = !this.muted
    },
    onVolume(e) {
      const v = Number(e.target.value)
      this.volume = v
      this.player.setVolume(v)
      if (v === 0) {
        this.player.mute()
        this.muted = true
      } else if (this.muted) {
        this.player.unMute()
        this.muted = false
      }
    },
    onSeekInput(e) {
      this.seeking = true
      this.currentTime = Number(e.target.value)
    },
    onSeekCommit(e) {
      this.player.seekTo(Number(e.target.value), true)
      this.seeking = false
    },
    goLive() {
      this.player.seekTo(this.player.getDuration(), true)
      this.player.playVideo()
    },
    showControls() {
      this.controlsVisible = true
      clearTimeout(this.hideTimer)
      this.hideTimer = setTimeout(() => {
        if (this.playing) this.controlsVisible = false
      }, 3000)
    },
    toggleFullscreen() {
      const el = this.$refs.wrapper
      const current = document.fullscreenElement || document.webkitFullscreenElement
      if (current) {
        (document.exitFullscreen || document.webkitExitFullscreen).call(document)
      } else {
        (el.requestFullscreen || el.webkitRequestFullscreen).call(el)
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement)
    },
    format(sec) {
      sec = Math.max(0, Math.floor(sec || 0))
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = String(sec % 60).padStart(2, '0')
      return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
    }
  }
}
</script>

<style>
/* Não-scoped de propósito: o iframe é criado pelo SDK e o Video.vue tem um estilo global para "iframe" */
.yt-wrapper {
  position: relative;
  width: 100%;
  background: #000;
  overflow: hidden;
  user-select: none;
}
.yt-wrapper.yt-fullscreen {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Área visível em 16:9 — precisa ser exata para o corte das faixas do YouTube funcionar */
.yt-stage {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
}
.yt-fullscreen .yt-stage {
  padding-top: 0;
  width: min(100vw, calc(100vh * 16 / 9));
  height: min(100vh, calc(100vw * 9 / 16));
}

/* iframe com o dobro da altura, deslocado 50% para cima */
.yt-frame {
  position: absolute;
  left: 0;
  top: -50%;
  width: 100%;
  height: 200%;
  pointer-events: none;
}
.yt-frame iframe {
  position: absolute;
  top: 0; left: 0;
  width: 100% !important;
  height: 100% !important;
  border: 0;
}
.yt-shield {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 2;
  cursor: pointer;
}
.yt-cover {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 3;
  background: #000 center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.yt-big-play {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: 0;
  background: rgba(0, 0, 0, .65);
  color: #fff;
  font-size: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform .15s, background .15s;
}
.yt-cover:hover .yt-big-play { transform: scale(1.08); background: rgba(0, 0, 0, .8); }

.yt-unmute {
  position: absolute;
  top: 12px; left: 12px;
  z-index: 4;
  border: 0;
  border-radius: 20px;
  padding: 6px 14px;
  background: rgba(0, 0, 0, .7);
  color: #fff;
  font-size: 14px;
}

.yt-controls {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  z-index: 4;
  padding: 18px 12px 6px;
  background: linear-gradient(transparent, rgba(0, 0, 0, .75));
  transition: opacity .3s;
}
.yt-controls-hidden { opacity: 0; pointer-events: none; }
.yt-wrapper:hover .yt-controls-hidden { opacity: 1; pointer-events: auto; }

.yt-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
}
.yt-btn {
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 22px;
  padding: 2px 6px;
  line-height: 1;
}
.yt-time { font-size: 13px; margin-left: 6px; font-variant-numeric: tabular-nums; }

.yt-live {
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: .5px;
  display: flex;
  align-items: center;
  margin-left: 6px;
}
.yt-live-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #e00;
  margin-right: 6px;
}
.yt-live-behind .yt-live-dot { background: #999; }

.yt-progress,
.yt-volume {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  margin: 0;
}
.yt-progress { width: 100%; height: 14px; display: block; }
.yt-volume { width: 80px; height: 14px; }
.yt-progress::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(to right, #00a19c var(--yt-progress, 0%), rgba(255, 255, 255, .35) var(--yt-progress, 0%));
}
.yt-progress::-moz-range-track { height: 4px; border-radius: 2px; background: rgba(255, 255, 255, .35); }
.yt-progress::-moz-range-progress { height: 4px; border-radius: 2px; background: #00a19c; }
.yt-volume::-webkit-slider-runnable-track { height: 4px; border-radius: 2px; background: rgba(255, 255, 255, .6); }
.yt-volume::-moz-range-track { height: 4px; border-radius: 2px; background: rgba(255, 255, 255, .6); }
.yt-progress::-webkit-slider-thumb,
.yt-volume::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px; height: 12px;
  margin-top: -4px;
  border-radius: 50%;
  background: #fff;
}
.yt-progress::-moz-range-thumb,
.yt-volume::-moz-range-thumb {
  width: 12px; height: 12px;
  border: 0;
  border-radius: 50%;
  background: #fff;
}
</style>
